import { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { WebView } from '@zenbusiness/application-commons-webview';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import ErrorMessageWithButton from '#client/components/ErrorMessageWithButton';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import LoadingContainerSpinner from '#client/components/LoadingContainerSpinner';
import { DepositMoneyInput } from '#client/components/MoneyInputs';
import { formatMoneyDisplay } from '#client/components/MoneyInputs/utils/format';
import TransactionDescription from '#client/components/MoveMoney/components/TransactionDescription';
import FieldReadView from '#client/components/ReadOrEdit/FieldReadView';
import FieldWithEditable from '#client/components/ReadOrEdit/FieldWithEditable';
import ViewAllTransactionLimitButton from '#client/components/ViewAllTransactionLimitButton';
import { DATE_FORMAT } from '#client/constants/date';
import { COUNTERPARTY_ID } from '#client/constants/modals';
import { IS_THAT_CORRECT_STEP, WHICH_ACCOUNT_STEP } from '#client/constants/transfer';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCounterpartyBalance from '#client/hooks/useCounterpartyBalance';
import useCounterpartyDetails from '#client/hooks/useCounterpartyDetails';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import usePlaid from '#client/hooks/usePlaid';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import useUnitAccountLimits from '#client/hooks/useUnitAccountLimits';
import { calculateTransferDays } from '#client/utils/date';
import { retrievedGraphQLError } from '#client/utils/errors';
import { capitalizeFirstLetter, getLast4digits } from '#client/utils/text';

import { DialogContentStyled, FieldContainerStyled, Wrapper } from './HowMuchForm.styles';

function HowMuchForm({ handleClose }) {
  const { isWebView } = WebView;
  const moneyInputRef = useRef(null);
  const { setCurrentFormStep, setData, data } = useFormStep();
  const { amount, description, selectedFromAccount, idempotencyKey } = data;
  const [moneyInputState, setMoneyInputState] = useState({
    value: amount,
    isError: false
  });

  const reviewButtonId = useEnterSubmit();
  const { createErrorToast, createCommonErrorToast } = useToastContext();
  const { unitAccountId } = useUnitAccountId();
  const [searchParams, setSearchParams] = useSearchParams();
  const counterpartyId = searchParams.get(COUNTERPARTY_ID);
  const { counterPartyData, fetchingCounterparty, counterpartyError } = useCounterpartyDetails({
    counterpartyId
  });
  const { businessEntityUuid } = useConstBusinessEntityUuid();

  const { remains, loading: loadingLimit, error: errorLimit, limit } = useUnitAccountLimits();
  const { dailyDebitRemain, monthlyDebitRemain } = remains;
  const { dailyDebit, monthlyDebit } = limit;

  const { unitAccount, loading: loadingUnitAccount, error: errorUnitAccount } = useUnitAccount();

  // Initialize the isFetching state based on the presence of counterpartyId
  // and whether the selectedFromAccount object has no keys.
  const [isFetching, setIsFetching] = useState(
    !!(
      (
        counterpartyId && // Check if counterpartyId is truthy
        Object.keys(selectedFromAccount).length === 0
      ) // Check if selectedFromAccount is an empty object
    )
  );
  const { accountNumber: toAccountNumber = '', depositProduct: toAccountType = '' } =
    unitAccount || {};
  const amountBalance = unitAccount?.availableBalance?.amount ?? {};

  useEffect(() => {
    if (counterpartyError) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartyError]);

  /**
   * useEffect hook for updating form data based on counterparty details.
   * This hook is triggered when the counterparty ID, counterparty data,
   * and other relevant conditions are met.
   */
  useEffect(() => {
    // Check if the form data should be updated
    const shouldUpdateFormData = Boolean(
      counterpartyId &&
      counterPartyData &&
      !fetchingCounterparty &&
      Object.keys(data?.selectedFromAccount).length === 0
    );

    // If conditions are met, update the form data
    if (shouldUpdateFormData) {
      setData((prevData) => ({
        ...prevData,
        selectedFromAccount: counterPartyData
      }));
      setIsFetching(false); // Set fetching status to false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterPartyData, counterpartyId, fetchingCounterparty, data?.selectedFromAccount]);

  useModalDisplayTracker({
    elementName: 'input_amount_dm_modal',
    elementText: 'input_amount_dm_modal',
    appPageName: 'active_banking_dashboard'
  });

  const {
    counterpartyBalance: {
      available: counterpartyAvailableValue,
      balance: counterpartyBalanceValue
    } = {},
    error: errorCounterpartyBalance,
    loading: loadingCounterparty,
    refetchCounterpartyBalance
  } = useCounterpartyBalance(selectedFromAccount?.id);

  // should show relink and validate the balance check
  // if the the error message: RELINK_REQUIRED ans status code: 400
  const isRelinkRequired = useMemo(() => {
    const message = errorCounterpartyBalance?.message;
    const statusCode = errorCounterpartyBalance?.statusCode;

    return statusCode === 400 && message === 'RELINK_REQUIRED';
  }, [errorCounterpartyBalance]);

  const shouldRelinkAccount = errorCounterpartyBalance && isRelinkRequired;
  const isLoading = loadingLimit || loadingUnitAccount || loadingCounterparty;

  const isDisabledReviewTransfer = !!(
    isLoading ||
    !moneyInputState.value ||
    moneyInputState.isError ||
    errorLimit ||
    errorUnitAccount ||
    shouldRelinkAccount
  );

  const initDate = useMemo(() => dayjs(new Date()).format(DATE_FORMAT.MOVE_MONEY), []);
  const expectedDate = dayjs(calculateTransferDays(initDate)).format(DATE_FORMAT.MOVE_MONEY);

  const trackReviewTransferClick = useButtonPressedCallback({
    elementName: 'review_transfer',
    elementText: 'review_transfer',
    appPageName: 'input_amount_dm_modal'
  });

  const handleReviewTransfer = () => {
    trackReviewTransferClick();

    // Validate money input (empty, limit)
    const isValid = moneyInputRef.current.validate();
    if (isValid) {
      setData({
        ...data,
        amount: moneyInputState.value,
        toAccount: unitAccount,
        initDate,
        expectedDate,
        idempotencyKey: idempotencyKey ?? uuidv4()
      });
      setCurrentFormStep(IS_THAT_CORRECT_STEP);
    }
  };

  useEffect(() => {
    if (shouldRelinkAccount) {
      createErrorToast({
        title: 'This account is no longer linked',
        description: 'Please relink the account and try again.',
        error: errorCounterpartyBalance
      });
      return;
    }

    const otherErrors = errorLimit || errorUnitAccount;
    if (otherErrors) {
      createCommonErrorToast({ error: otherErrors });
      handleClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorLimit, errorUnitAccount, errorCounterpartyBalance]);

  // refetch counterpartyBalance to update the data
  const onCounterPartyReceived = () => {
    refetchCounterpartyBalance();
  };

  const { isUpdatingPlaidLinkToken, updatePlaidLinkToken, isHandling } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    onCounterPartyReceived,
    isRelink: true,
    counterPartyId: selectedFromAccount?.id
  });

  const relinkHandler = useCallback(async () => {
    const { error } = await updatePlaidLinkToken();
    // handle show error
    if (error) {
      createErrorToast({
        title: retrievedGraphQLError(error),
        error
      });
    }
  }, [createErrorToast, updatePlaidLinkToken]);

  const isDisableConnectOrRelink = isUpdatingPlaidLinkToken || isHandling;

  const blurHandler = useCallback(
    (event) => {
      setData({
        ...data,
        description: event?.target?.value || ''
      });
    },
    [data, setData]
  );

  const trackEditClick = useButtonPressedCallback({
    elementName: 'edit_account',
    elementText: 'edit',
    appPageName: 'input_amount_dm_modal'
  });

  const editFromSectionHandler = useCallback(() => {
    // if in the URL has counterpartyId
    // we should clear the counterpartyId when user click edit
    trackEditClick();
    if (counterpartyId) {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete(COUNTERPARTY_ID);
          return newParams;
        },
        { replace: true }
      );
    }
    setCurrentFormStep(WHICH_ACCOUNT_STEP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartyId, setCurrentFormStep, setSearchParams]);

  const fromTitle = `${selectedFromAccount?.bank}`;
  const fromValue = `${capitalizeFirstLetter(selectedFromAccount?.accountType)} ${getLast4digits(
    selectedFromAccount?.accountNumber
  )}`;

  const toTitle = loadingUnitAccount ? (
    <Skeleton with="100%" />
  ) : (
    `ZenBusiness Banking • ${formatMoneyDisplay(amountBalance / 100)}`
  );
  const toValue = loadingUnitAccount ? (
    <Skeleton with="100%" />
  ) : (
    `${capitalizeFirstLetter(toAccountType)} ${getLast4digits(toAccountNumber)}`
  );

  return (
    <>
      <DialogContentStyled role="form">
        <ContainerGrid>
          <FocusGrid>
            <Wrapper gap={{ xs: '32px', md: '40px' }}>
              {isFetching ? (
                <LoadingContainerSpinner />
              ) : (
                <>
                  <Heading
                    family="secondary"
                    size="3xl"
                    weight="bold"
                  >
                    How Much?
                  </Heading>
                  <Stack gap="16px">
                    <DepositMoneyInput
                      bankName={selectedFromAccount?.bank}
                      counterpartyBalance={counterpartyAvailableValue || counterpartyBalanceValue}
                      dailyLimit={dailyDebit}
                      dailyRemainLimit={dailyDebitRemain}
                      defaultValue={moneyInputState.value}
                      handleDataChange={setMoneyInputState}
                      isVerifyConuterpartyBalance={isRelinkRequired}
                      monthlyLimit={monthlyDebit}
                      monthlyRemainLimit={monthlyDebitRemain}
                      ref={moneyInputRef}
                      title="Deposit Amount"
                    />
                    <ViewAllTransactionLimitButton />
                  </Stack>
                  <Stack spacing="8px">
                    <Text
                      size="md"
                      weight="medium"
                    >
                      From
                    </Text>
                    <FieldContainerStyled>
                      <FieldWithEditable
                        onEdit={editFromSectionHandler}
                        title={fromTitle}
                        value={fromValue}
                      />
                    </FieldContainerStyled>
                    {shouldRelinkAccount && (
                      <ErrorMessageWithButton
                        buttonText="Relink"
                        disabled={isDisableConnectOrRelink}
                        hasButton={!isWebView}
                        message="This account is no longer linked."
                        onClick={relinkHandler}
                      />
                    )}
                  </Stack>
                  <Stack spacing="8px">
                    <Text
                      color="primary"
                      size="md"
                      weight="medium"
                    >
                      To
                    </Text>
                    <FieldContainerStyled>
                      <FieldReadView
                        title={toTitle}
                        value={toValue}
                      />
                    </FieldContainerStyled>
                  </Stack>
                  <Stack spacing="8px">
                    <Text
                      color="primary"
                      size="md"
                      weight="medium"
                    >
                      Transfer type
                    </Text>
                    <FieldContainerStyled>
                      <FieldReadView
                        title="ACH"
                        value={`Expected ${expectedDate}`}
                      />
                    </FieldContainerStyled>
                  </Stack>
                  <TransactionDescription
                    maxLength={50}
                    onBlur={blurHandler}
                    placeholder="Add a note for the recipient"
                    title="Transaction Description (optional)"
                    value={description}
                  />
                </>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      {!isFetching && (
        <ActionButtonsGrid>
          <LoadingButton
            color="primary"
            disabled={isDisabledReviewTransfer}
            fullWidth
            id={reviewButtonId}
            loading={isLoading}
            onClick={handleReviewTransfer}
            size="large"
            variant="contained"
          >
            Review transfer
          </LoadingButton>
        </ActionButtonsGrid>
      )}
    </>
  );
}

export default memo(HowMuchForm);

HowMuchForm.propTypes = {
  handleClose: PropTypes.func
};
